import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
  EventEmitter
} from "@angular/core";
import {
  StatGroup,
  StatGroupResult,
  StatMetric,
  TableColumn,
} from "../table-view/table-view-component-base";
import { asyncScheduler, Subscription } from "rxjs";
import { ListViewDirectiveOf } from "../../directives/list-view.directive";

@Component({
  selector: "fes-stats-view",
  styleUrls: ["./stats-view.component.scss"],
  templateUrl: "./stats-view.component.html",
})
export class StatsViewComponent implements OnDestroy, AfterViewInit {
  @Input() tableColumns: TableColumn[] = [];
  @Input() maxNoFilterRows: number = 1;
  @Input() maxNoFilterColumns: number = 1;
  @Input() isVisible: boolean = true;
  @Input() loadStatsFn: ( statGroups: StatGroup[] ) => Promise<StatGroupResult[]> | undefined;
  @Input('statGroupResults') set setStatGroupResults(statGroupResults: StatGroupResult[]) {
    this.statGroupResults = statGroupResults;
    // wait for tableColumns to be set before calling init...
    asyncScheduler.schedule(()=>this.init());
  }
  statGroupResults: StatGroupResult[] = [];
  @Output('statGroupResultChanged') statGroupResultChanged = new EventEmitter<StatGroupResult>();
  @ViewChildren("fesListView", { read: ListViewDirectiveOf })
  fesListViewDirectives!: QueryList<ListViewDirectiveOf<any, any>>;
  private queryListSubscription?: Subscription;

  sortTypeByStatDataType: { [key: string]: "numeric" | "string" } = {
    number: "numeric",
    date: "string",
  };

  sortTypesByColumnType: { [key: string]: "numeric" | "string" } = {
    number: "numeric",
    date: "string",
  };

  columnTitles: { [key: string]: string | undefined } = {};

  constructor(private cdr: ChangeDetectorRef) {}

  init(): void {
    // sorts should either be numeric or string (db aggregate functions only work on numeric or date)
    this.columnTitles = {};
    this.sortTypesByColumnType = {};
    this.loadingStatsById = {};
    // console.log('init() tableColumns',this.tableColumns);
    for (const column of this.tableColumns ?? []) {
      if (
        ["time", "date", "fulltime", "localtime", "localdate"].includes(
          column.type
        )
      ) {
        this.sortTypesByColumnType[column.name] = "string";
      } else if (["number", "percent", "numeric"].includes(column.type)) {
        this.sortTypesByColumnType[column.name] = "numeric";
      }
      this.columnTitles[column.name] = column.title;
    }
    for (const statGroup of this.statGroupResults ?? []) {
      if (!statGroup.title) {
        statGroup.title = statGroup.groupBy
          .map((column) => this.columnTitles[column])
          .join("/");
      }
      for (const metric of statGroup.metrics ?? []) {
        metric.title =
          metric.title ?? this.columnTitles[metric.column] ?? metric.column;
        if (!(metric.column in this.sortTypesByColumnType)) {
          const hasDateSuffix = /(date|time)$/i.test(metric.column);
          this.sortTypesByColumnType[metric.column] = hasDateSuffix
            ? "string"
            : "numeric";
        }
      }
    }
  }

  ngAfterViewInit() {
    // Listen for changes in fesListView directive instances
    this.queryListSubscription = this.fesListViewDirectives.changes.subscribe(
      () => {
        this.onListViewAdded();
      }
    );

    // Initial call for already present instances
    this.onListViewAdded();
  }

  ngOnDestroy() {
    this.queryListSubscription?.unsubscribe();
  }

  private onListViewAdded() {
    this.cdr.detectChanges();
    this.fesListViewDirectives.forEach((fesListViewDirective, index) => {
      //console.log("fesListViewDirective", fesListViewDirective,index);
      const statGroup = this.statGroupResults[index];
      asyncScheduler.schedule(() => {
        for (const column of (statGroup.groupBy ?? []).slice().reverse()) {
          fesListViewDirective.onSort({
            name: column,
            direction: "asc",
          });
        }
      });
    });
  }

  formatStatValue(value: any, numberFormat: string): string {
    const numValue = typeof value === "number"
      ? value
      : typeof value === "string" && /^-?\d*\.?\d+$/.test(value)
        ? parseFloat(value)
        : null;

    if (numValue !== null) {
      const formatParts = numberFormat.split(".");
      if (formatParts.length === 2) {
        const integerDigits = parseInt(formatParts[0]) || 1;
        const fractionParts = formatParts[1].split("-");
        const minFractionDigits = parseInt(fractionParts[0]) || 0;
        const maxFractionDigits = parseInt(fractionParts[1]) || 0;

        return new Intl.NumberFormat("en-US", {
          minimumIntegerDigits: integerDigits,
          minimumFractionDigits: minFractionDigits,
          maximumFractionDigits: maxFractionDigits,
        }).format(numValue);
      }
    }
    return value;
  }


  onVisibleMetricsChange(
    visibleMetrics: StatMetric[],
    allGroupMetrics: StatMetric[]
  ) {
    allGroupMetrics.forEach((metric) => (metric.hidden = true));
    visibleMetrics.forEach((metric) => (metric.hidden = false));
  }

  loadingStatsById: { [key: string]: boolean } = {};

  async toggleGroupVisible( statGroupResult: StatGroupResult) {
    statGroupResult.visible=!statGroupResult.visible;
    const srcStatGroup = this.statGroupResults.find(
      (g) => g.id === statGroupResult.id
    );
    if ( srcStatGroup ) {
      srcStatGroup.visible = statGroupResult.visible;
    }
    if ( this.loadStatsFn && statGroupResult.visible && statGroupResult.rows.length === 0 ) {
      try {
        this.loadingStatsById[statGroupResult.id] = true;
        const newStatGroupResult = await this.loadStatsFn( [ statGroupResult] );
        statGroupResult.rows = newStatGroupResult[0].rows;
        // console.log('toggleGroupVisible() emitting new result', statGroupResult);
      } catch ( e: any ) {
        console.error('Error loading stats for group', statGroupResult, e);
      } finally {
        this.loadingStatsById[statGroupResult.id] = false;
      }
    }
    this.statGroupResultChanged.emit(statGroupResult);
  }
}
